/**
 * EmptyState.js
 *
 * Native-style empty / error / loading placeholder.
 * Matches the empty-state pattern used in the native screens/.
 *
 * Usage:
 *   <EmptyState
 *     icon={<AssignmentIcon sx={{ fontSize: 56 }} />}
 *     title="No Assignments"
 *     subtitle="You have no pending assignments."
 *   />
 *
 *   <EmptyState
 *     icon={<ErrorOutlineIcon sx={{ fontSize: 56 }} />}
 *     title="Something Went Wrong"
 *     subtitle="Please try again."
 *     actionLabel="Retry"
 *     onAction={refetch}
 *   />
 */
import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import colors from '../common/Colors';

const EmptyState = ({
  icon = <InboxIcon sx={{ fontSize: 56 }} />,
  title = 'Nothing here',
  subtitle = '',
  actionLabel = '',
  onAction = null,
  iconColor = colors.BLUE,
  sx = {},
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 3,
        textAlign: 'center',
        ...sx,
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: `${iconColor}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          color: iconColor,
          '& svg': { fontSize: 40 },
        }}
      >
        {icon}
      </Box>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          fontSize: '16px',
          color: '#1A1A2E',
          mb: 0.5,
        }}
      >
        {title}
      </Typography>

      {subtitle ? (
        <Typography
          variant="body2"
          sx={{
            fontSize: '13px',
            color: '#888888',
            maxWidth: 260,
          }}
        >
          {subtitle}
        </Typography>
      ) : null}

      {actionLabel && onAction ? (
        <Button
          variant="contained"
          onClick={onAction}
          sx={{
            mt: 2.5,
            borderRadius: 8,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: iconColor,
            '&:hover': { backgroundColor: iconColor, opacity: 0.9 },
          }}
        >
          {actionLabel}
        </Button>
      ) : null}
    </Box>
  );
};

export default EmptyState;
